<template lang="pug">
  product-page-v2(:product="product" :buy-request="buyRequest")
    template(#media)
      .nfl-media
        .thumbs.custom-scroll(v-drag-scroll="{vertical: thumbsVertical}")
          DynamicMedia.thumb(
            v-for="obj in thumbnails"
            :key="obj.weight"
            :config="{src: obj.src, width: 50, height: 50}" theme="center"
            @click="config.weight = obj.weight"
            :class="{selected: config.weight === obj.weight}"
            :widths="[100]"
          )
            .nfl-overlay(:class="'custom-plate-'+config.bumperType")
              DynamicMedia(:config="{src: obj.overlay, width: 50, height: 50}" :widths="[100]")
        .nfl-stage
          .bg-img
            DynamicMedia(:config="{src: baseImageUrl, width: 2048, height: 2048}" theme="center")
              .nfl-overlay(:class="'custom-plate-'+config.bumperType")
                DynamicMedia(:config="{src: overlayImageUrl, width: 2048, height: 2048}")
    template(#rhpa)
      product-badges.rhpa-badges(:buyRequest="buyRequest")
      Swatches(label="Bumper Type" :options="BumperTypeOptions" v-model="config.bumperType"  @update:modelValue="changeBumper")
      Swatches(label="Choose Your Team" :options="TeamOptions" v-model="config.team" code="team" @update:modelValue="changeTeam")
      Swatches(label="Logo Options" :options="LogoOptions" v-model="config.logo")
      Swatches(label="Change Color" v-if="ColorOptions.length" :options="ColorOptions" v-model="config.color")
      Accordion(theme="rhpa")
        template(#heading)
          .accordion-header
            Icon.left-icon(fill="black" name="misc/weight-variant")
            h3 Pick Weight Variants
        rhpa-body(v-if="buyRequest" :buyRequest="buyRequest")
      minimum-weight(:minimumWeight="MIN_WEIGHT" :addedWeight="weightAdded")
      .rhpa-footer
        rhpa-footer(
          v-if="buyRequest"
          :buy-request="buyRequest"
          :disabled="weightAdded < MIN_WEIGHT"
          :force-show-price="true"
          :validate="addRecipes"
        )
    template(#tabs)
      RenderBlock(:block="slots.tabs" :product="selectedGroup" :selectedSimple="selected45" v-if="(slots.tabs || []).length")
    template(#mainslot)
      RenderBlock(:block="slots.main" :product="selectedGroup" :selectedSimple="selected45")
</template>
<script setup lang="ts">
import { onMounted, reactive, ref, computed, onServerPrefetch, watch, defineAsyncComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useMQ } from 'sisu/js/mq'
import { useRogue } from '@/js/services/Rogue'
import { useProductStore } from '@/js/stores/productStore'
import { createBuyRequest } from '@/js/helpers/buy-request'
import type { BuyRequest, Product } from '@/js/services/RogueTypes'
import ProductPageV2 from '@/js/pages/product/product-page-v2.vue'

const RhpaBody = defineAsyncComponent(() => import('@/js/components/rhpa/rhpa-body.vue'))
const RhpaFooter = defineAsyncComponent(() => import('../../../components/rhpa/rhpa-footer.vue'))
const ProductBadges = defineAsyncComponent(() => import('../../../components/rhpa/badges.vue'))
const minimumWeight = defineAsyncComponent(() => import('../custom/minimum-weight.vue'))


// Minimum weight before you can Add to Cart
const MIN_WEIGHT = 200

const productStore = useProductStore()
const rogue = useRogue()
const route = useRoute()

// We pull in the product to load the page content
const props = defineProps<{product: Product}>()

const slots = computed(() => ((props.product?.roguebuilder || {}).slots || {}))

const cacheBust = route.query.nocache ? `?v=${Date.now()}` : '?v=4'

// Utility function to find the specific background image for the bumper type and visible weight selection
function getBaseImage(bumperType: string, weight: number) {
  let folder = '/zeus/_____/bumpers'

  if (bumperType.startsWith('black')) {
    folder += '/v2'
  }

  // 10lb color urethanes are actually black
  if (bumperType === 'color_urethane' && weight === 10) {
    bumperType = 'black_urethane'
  }

  const parts = [bumperType]
  if (['black_training', 'black_urethane'].indexOf(bumperType) === -1) {
    parts.push(`${weight}`)
  }

  return `${folder}/${parts.join('_')}.png${route.query.nocache === 'all' ? cacheBust : '?v=4'}`
}

const BUMPERS:{productId:number, label: string, key: string, recipe: string}[] = [
  { productId: 1128843, label: 'HG', key: 'hg', recipe: 'hg' },
  { productId: 1119419, label: 'Black Training', key: 'black_training', recipe: 'bt' },
  { productId: 1119420, label: 'Color Training', key: 'color_training', recipe: 'ct' },
  { productId: 1119421, label: 'Black Urethane', key: 'black_urethane', recipe: 'bu' },
  { productId: 1119422, label: 'Color Urethane', key: 'color_urethane', recipe: 'cu' },
]

const TEAMS = ['Arizona Cardinals', 'Atlanta Falcons', 'Baltimore Ravens', 'Buffalo Bills', 'Carolina Panthers',
  'Chicago Bears', 'Cincinnati Bengals', 'Cleveland Browns', 'Dallas Cowboys', 'Denver Broncos', 'Detroit Lions', 'Green Bay Packers',
  'Houston Texans', 'Indianapolis Colts', 'Jacksonville Jaguars', 'Kansas City Chiefs', 'Las Vegas Raiders',
  'Los Angeles Chargers', 'Los Angeles Rams', 'Miami Dolphins', 'Minnesota Vikings', 'New England Patriots', 'New Orleans Saints', 'New York Giants',
  'New York Jets', 'Philadelphia Eagles', 'Pittsburgh Steelers', 'San Francisco 49ers', 'Seattle Seahawks', 'Tampa Bay Buccaneers',
  'Tennessee Titans', 'Washington Commanders']

function nameToKey(name: string) {
  // For team name we strip off the city and take just the last word (Packers, Browns etc)
  const teamName = name.split(' ').pop()

  return name[0] + teamName?.substring(0, 3)
}

const defaultConfig = {
  bumperType : 'hg',
  team       : 'Arizona Cardinals',
  logo       : 'a',
  color      : 'white',
  weight     : 25,
}

// Read from the recipe Id in the query param if it exists
if (route.query.rid) {
  const [_nfl, type, weight, team, logo, color] = (route.query.rid as string).split('-')

  defaultConfig.bumperType = BUMPERS.find(b => b.recipe === type?.toLowerCase())?.key || 'hg'
  defaultConfig.weight = Number(weight)
  defaultConfig.team = TEAMS.find(t => nameToKey(t).toUpperCase() === team) || defaultConfig.team
  defaultConfig.color = color?.toLowerCase() === 'b' ? 'black' : 'white'
  defaultConfig.logo = logo?.toLowerCase()
}

// This is the state for the RHPA. Changing the swatches will update this state object, which will recreate the buy request / update the UI
const config = reactive(defaultConfig)

// this is going to be dynamic based on the bumper type selected. Its actually going to recreate a buy request with a new group parent each time you change...
const buyRequest = ref<null | BuyRequest>(null)

// This is used to show dynamic gear specs, etc
const selectedGroup = computed(() => {
  const bumperProductId = BUMPERS.find(b => b.key === config.bumperType)?.productId

  return rogue.getProductById(bumperProductId || 0)
})
const selected45 = computed(() => rogue.getProductById(buyRequest.value?.children?.[buyRequest.value.children.length - 2].productId || 0))

// Find the specific product and create a buy request for the bumper group
async function updateBuyRequest() {
  const bumper = BUMPERS.find(b => b.key === config.bumperType)

  if (!bumper) {
    buyRequest.value = null

    return
  }

  if (!rogue.getProductById(bumper.productId) || !productStore.allChildrenLoaded(bumper.productId)) {
    // We only want to run this `await` if the children are not already loaded, otherwise we can create a hydration error
    await productStore.loadAllChildren(bumper.productId, rogue)
  }

  // Save the selected weights, so we can reselect them on the new bumper type (if possible)
  const selectedWeights:{[key: number]: number} = {}

  buyRequest.value?.children?.forEach(child => selectedWeights[rogue.getProductById(child.productId).weight || 0] = child.qty)

  // Create a new buy request, with the new parent groupId
  const newBuyRequest = createBuyRequest(rogue.getProductById(bumper.productId), rogue)

  // Reselect the correct weights based on the previous values
  newBuyRequest.children?.forEach(child => {
    const p = rogue.getProductById(child.productId)
    child.qty = selectedWeights[p.weight || -1] || 0
  })

  buyRequest.value = newBuyRequest
}

// This will cause the server to preload the required products, and create an initial buy request
onServerPrefetch(() => updateBuyRequest())

// Client side we need to create the buy request on load
updateBuyRequest()

// When the config changes (the combination of the RHPA options), we reload the buy request
watch(() => config, () => updateBuyRequest(), { deep: true })

function keepLogo(team: string, fromType: string, toType: string) {
  const from = artworkLetter(team, fromType, config.logo)
  const version = logoVersion(team, logoFamily(fromType), from)

  const family = logoFamily(toType)
  const artwork = artworkLetters(team, toType)
  const match = artwork.findIndex(letter => logoVersion(team, family, letter) === version)

  config.logo = optionLetters(team, toType)[match === -1 ? 0 : match]
}

let selectedBumperType = defaultConfig.bumperType

// Make sure our configuration is valid for each bumper type we select
function changeBumper(newVal: string) {
  keepLogo(config.team, selectedBumperType, newVal)
  selectedBumperType = newVal

  // Training plates dont have 10lb weight
  if (config.weight === 10 && newVal.includes('training')) {
    config.weight = 25
  }

  // Only HGs have 15s - all else needs to switch
  if (config.weight === 15 && newVal !== 'hg') {
    config.weight = 25
  }
}

function changeTeam(newVal: string) {
  const family = logoFamily(selectedBumperType)
  const from = artworkLetter(config.team, selectedBumperType, config.logo)
  const version = logoVersion(config.team, family, from)

  const artwork = artworkLetters(newVal, selectedBumperType)
  const match = artwork.findIndex(letter => logoVersion(newVal, family, letter) === version)

  config.logo = optionLetters(newVal, selectedBumperType)[match === -1 ? 0 : match]
}

// This is the "Rogue Related" section on the page, that shows a thumbnail per bumper type
const BumperTypeOptions = BUMPERS.map(opt => ({ label: opt.label, value: opt.key, image: getBaseImage(opt.key, 45) }))

// Team selection has icons for each team
const TeamOptions = TEAMS.map(name => ({ label: name, value: name, imgAttr: `team/${name.replace(/ /g, '')}` }))

type LogoFamily = 'hg' | 'black' | 'color'

function logoFamily(bumperType: string) {
  return bumperType.split('_')[0] as LogoFamily
}

const LOGOS: Record<LogoFamily, string[]> = {
  hg    : ['a', 'c', 'b'],
  black : ['a', 'b', 'c'],
  color : ['a', 'b'],
}

const EXTRA_LOGOS: Record<string, string[]> = {
  'Baltimore Ravens'     : ['d', 'e'],
  'Carolina Panthers'    : ['d'],
  'Chicago Bears'        : ['d'],
  'Cincinnati Bengals'   : ['d'],
  'Cleveland Browns'     : ['d', 'e', 'f'],
  'Denver Broncos'       : ['d'],
  'Detroit Lions'        : ['d'],
  'Indianapolis Colts'   : ['d'],
  'Miami Dolphins'       : ['d'],
  'Minnesota Vikings'    : ['d'],
  'New England Patriots' : ['d'],
  'New Orleans Saints'   : ['d'],
  'Philadelphia Eagles'  : ['d'],
  'Seattle Seahawks'     : ['d'],
  'Tampa Bay Buccaneers' : ['d'],
}

const EXTRA_COLOR_LOGOS: Record<string, string[]> = {
  'Baltimore Ravens'     : ['e', 'f'],
  'Carolina Panthers'    : ['e'],
  'Chicago Bears'        : ['e'],
  'Cincinnati Bengals'   : ['e'],
  'Cleveland Browns'     : ['e', 'f', 'g'],
  'Detroit Lions'        : ['e'],
  'Indianapolis Colts'   : ['e'],
  'Miami Dolphins'       : ['e'],
  'Minnesota Vikings'    : ['e'],
  'New England Patriots' : ['e'],
  'New Orleans Saints'   : ['e'],
  'Philadelphia Eagles'  : ['e'],
  'Seattle Seahawks'     : ['e'],
  'Tampa Bay Buccaneers' : ['e'],
}

const LEGACY_THUMBS: Record<LogoFamily, Record<string, string>> = {
  hg    : { a: 'color_logo', b: 'color_logo_white', c: 'color_word' },
  black : { a: 'color_logo', b: 'color_word', c: 'color_logo_white' },
  color : { a: 'black_logo', b: 'black_word' },
}

function nickname(team: string) {
  return (team.split(' ').pop() || '').toLowerCase()
}

function logoThumb(family: LogoFamily, letter: string, team: string) {
  if (isUpdated(letter)) {
    return `${LEGACY_THUMBS[family][baseLetter(letter)]}_2026`
  }

  if (!extraLetters(team, family).includes(letter)) {
    return LEGACY_THUMBS[family][letter]
  }

  if (family === 'color') {
    return `${nickname(team)}-black-${logoVersion(team, family, letter)}`
  }

  return `${nickname(team)}-${letter.charCodeAt(0) - 'a'.charCodeAt(0) + 1}`
}

const COLOR_WHITE_LETTER: Record<string, string> = { a: 'c', b: 'd' }

const EXTRA_COLOR_WHITE: Record<string, Record<string, string>> = {
  'Baltimore Ravens'     : { e: 'g', f: 'h' },
  'Carolina Panthers'    : { e: 'f' },
  'Chicago Bears'        : { e: 'f' },
  'Cincinnati Bengals'   : { e: 'f' },
  'Cleveland Browns'     : { e: 'h', f: 'i', g: 'j' },
  'Detroit Lions'        : { e: 'f' },
  'Indianapolis Colts'   : { e: 'f' },
  'Miami Dolphins'       : { e: 'f' },
  'Minnesota Vikings'    : { e: 'f' },
  'New England Patriots' : { e: 'f' },
  'New Orleans Saints'   : { e: 'f' },
  'Philadelphia Eagles'  : { e: 'f' },
  'Seattle Seahawks'     : { e: 'f' },
  'Tampa Bay Buccaneers' : { e: 'f' },
}

function colorWhite(team: string, letter: string) {
  const black = baseLetter(letter)
  const white = EXTRA_COLOR_WHITE[team]?.[black] || COLOR_WHITE_LETTER[black] || black

  return isUpdated(letter) ? white + UPDATED_SUFFIX : white
}

const UPDATED_2026: Record<string, number[]> = {
  'Los Angeles Rams' : [2],
  'Tennessee Titans' : [1, 2, 3],
}

const UPDATED_SUFFIX = '1'
const UPDATED_FOLDER = '2026 Update'

// Artwork letters are always a single character, so a longer token is a re-shot option
function isUpdated(letter: string) {
  return letter?.length > 1 && letter.endsWith(UPDATED_SUFFIX)
}

// The letter as it appears in Cloudinary, with the marker taken back off
function baseLetter(letter: string) {
  return isUpdated(letter) ? letter.slice(0, -UPDATED_SUFFIX.length) : letter
}

function extraLetters(team: string, family: LogoFamily) {
  return (family === 'color' ? EXTRA_COLOR_LOGOS[team] : EXTRA_LOGOS[team]) || []
}

// The letters the Cloudinary paths are built from, in swatch order
function artworkLetters(team: string, bumperType: string) {
  const family = logoFamily(bumperType)
  const updated = UPDATED_2026[team] || []

  // LOGOS is in version order, so the index is the version the letter holds
  return LOGOS[family]
    .map((letter, i) => (updated.includes(i + 1) ? letter + UPDATED_SUFFIX : letter))
    .concat(extraLetters(team, family))
}

// What a swatch publishes, and what config.logo holds — the same token that ends up in the recipe.
// Dark plates use the artwork letter directly; those ids have been minted that way for years, HG's
// a/c/b ordering included. Colour plates use the swatch position instead, because a colour option
// spends two artwork letters, one per ink, so the artwork runs ahead of the swatch it sits on: the
// Browns' fifth colour option is drawn from color/g but reads NFL-CU-25-CBRO-E-B.
function optionLetters(team: string, bumperType: string) {
  const artwork = artworkLetters(team, bumperType)

  if (logoFamily(bumperType) !== 'color') {
    return artwork
  }

  return artwork.map((letter, i) => String.fromCharCode('a'.charCodeAt(0) + i) + (isUpdated(letter) ? UPDATED_SUFFIX : ''))
}

// The artwork behind an option, for building the Cloudinary path
function artworkLetter(team: string, bumperType: string, option: string) {
  if (logoFamily(bumperType) !== 'color') {
    return option
  }

  const position = baseLetter(option).charCodeAt(0) - 'a'.charCodeAt(0)

  return artworkLetters(team, bumperType)[position] || option
}

const FIRST_NEW_VERSION = 4

function logoVersion(team: string, family: LogoFamily, letter: string) {
  const base = LOGOS[family].indexOf(baseLetter(letter))

  if (base !== -1) {
    return base + 1
  }

  const extra = extraLetters(team, family).indexOf(letter)

  return extra === -1 ? 0 : extra + FIRST_NEW_VERSION
}

// This computes the RHPA thumb image for the selected bumper type for the "Logo Options" swatches
function rhpaImg(type:string) {
  return `/zeus/_____/nfl/${config.team.replace(/ /g, '')}/rhpa/${type}.png${cacheBust}`
}

// The logo options are based on the files from Creative...
const LogoOptions = computed(() => {
  const family = logoFamily(config.bumperType)
  const artwork = artworkLetters(config.team, config.bumperType)

  return optionLetters(config.team, config.bumperType).map((letter, i) => ({
    label : `Logo ${String.fromCharCode(65 + i)}`,
    value : letter,
    image : rhpaImg(logoThumb(family, artwork[i], config.team)),
  }))
})

// If we have Color Bumper selected, they get an additional selection for White vs Black on the logo
const ColorOptions = computed(() => (config.bumperType.startsWith('color') ? [
  { label: 'White', value: 'white' },
  { label: 'Black', value: 'black' },
] : []))

function getOverlayImageForWeight(weight: number) {
  const bumperType = logoFamily(config.bumperType)

  let logo = artworkLetter(config.team, config.bumperType, config.logo)

  if (bumperType === 'color' && (config.color === 'white' || weight === 10)) {
    logo = colorWhite(config.team, logo)
  }

  const path = isUpdated(logo) ? `${UPDATED_FOLDER}/${baseLetter(logo)}` : logo
  const folder = `/zeus/_____/nfl/${config.team.replace(/ /g, '')}/${bumperType}/${path}`

  if (bumperType === 'hg') {
    return `${folder}.svg${cacheBust}`
  }

  return `${folder}/${weight}.svg${cacheBust}`
}

// Get the base image for the main media area
const baseImageUrl = computed(() => getBaseImage(config.bumperType, config.weight))

// Get the SVG of the overlay (based on the current weight selected)
const overlayImageUrl = computed(() => getOverlayImageForWeight(config.weight))

const mq = useMQ()
const thumbsVertical = computed(() => ['mobile', 'tablet'].indexOf(mq.value) === -1)

const thumbnails = computed(() => buyRequest.value?.children?.map(child => (rogue.getProductById(child.productId)?.weight || 0) / 2)
  .filter(weight => !!weight)
  .map(weight => ({
    weight,
    src     : getBaseImage(config.bumperType, weight),
    overlay : getOverlayImageForWeight(weight),
  })))

// Determine the total amount of weight in the RHPA, for the Min Weight Calculator in RHPA that disables checkout
const weightAdded = computed(() => (buyRequest.value?.children || [])
  .map(child => child.qty * (rogue.getProductById(child.productId).weight || 0))
  .reduce((sum, weight) => Math.min(MIN_WEIGHT, sum + weight), 0))

onMounted(() => {
  // Load all childen on mounted, so it wont flash when changing types
  BUMPERS.forEach(bumperType => productStore.loadAllChildren(bumperType.productId, rogue))
})

// Before Add to Cart, we want to set the recipeIds on these plates
async function addRecipes(target = buyRequest.value) {
  const recipesToCreate:{[key: string] : {img: string, overlay: string, sku: string, price: number}} = {}

  // This works different from normal Zeus bumpers - normally we upload files to the Zeus server, which then creates a recipe id and returns it
  // In this case, since the recipes and files are already known, we attach the recipeIds directly on client side
  target?.children?.filter(child => child.qty > 0)
    .forEach(child => {
      child.metadata = child.metadata || {}
      const product = rogue.getProductById(child.productId)

      const teamKey = nameToKey(config.team)

      const weight = (product.weight || 0) / 2

      const bumperKey = BUMPERS.find(b => b.key === config.bumperType)?.recipe || config.bumperType

      // Prefixing with NFL lets us find these in reporting
      const recipeParts = ['NFL', bumperKey, weight, teamKey, config.logo]
      if (config.bumperType.startsWith('color')) {
        recipeParts.push(config.color === 'white' ? 'w' : 'b')
      }

      const recipeId = recipeParts.join('-').toUpperCase()

      child.metadata.recipeId = recipeId
      child.metadata.nonFluidCustomization = true
      child.metadata.nflBumper = { team: config.team, logo: config.logo }

      // Recipe Hash is used to make sure they are over 200lb minimum to allow the purchase
      child.metadata.recipeHash = `${bumperKey}${teamKey}${config.logo}`

      recipesToCreate[recipeId] = {
        img     : getBaseImage(config.bumperType, weight),
        overlay : getOverlayImageForWeight(weight),
        sku     : product.sku as string,
        price   : product.price as number,
      }
    })

  // Make sure the ZEUS server has created these recipes already
  try {
    await rogue.fetch('/svc_zeus/nfl_bumper_recipes', 'POST', recipesToCreate)
  } catch (e) {
    rogue.log.error('Error creating Zeus NFL bumper recipes', e as Error, { data: recipesToCreate })
  }

  return true
}

</script>
<style lang="scss" scoped>

.bg-img:deep(.content.center) {
  padding: 0;
}

.nfl-overlay {
  width: 82.5%;
  height: 82.5%;

  &.custom-plate-hg {
    width: 74%;
    height: 74%;
  }
}

.rhpa-badges {
  padding: $space-s 0;
  border-bottom: 1px solid $color-gray-light;
  margin-bottom: $space-s;
}

:deep(.pp-media) {
  @include not-desktop() {
    aspect-ratio: auto;
  }
}

.nfl-media {
  position: relative;
  width: 100%;

  @include not-desktop() {
    display: flex;
    flex-direction: column;
  }
}

.nfl-stage {
  position: relative;
  padding-top: 56.25%;
  width: 100%;
}

.bg-img {
  position: absolute;
  top: 50%;
  left: 50%;
  height: 100%;
  aspect-ratio: 1/1;
  transform: translate(-50%, -50%);
}

.rhpa-footer {
  margin-top: $space-s;
  border-top: 1px solid  $color-gray-light;
  padding: $space-s 0;
}

.overlay-svg {
  width: 100%;
  height: 100%;
}

.accordion-header {
  display: flex;
  align-items: center;

  h3 {
    margin-bottom: 0;
  }

  .left-icon {
    width: 20px;
    height: 32px;
  }
}

.thumbs {
  @include all-desktop() {
    position: absolute;
    top: 0;
    left: $space-s;
    padding-top: $space-s;
    width: 110px;
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
  }

  @include not-desktop() {
    order: 2;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0 $space-s $space-xs;
    overflow-x: auto;
  }

  .thumb {
    flex-shrink: 0;
    width: 100px;
    height: 100px;
    cursor: pointer;
    margin-bottom: $space-xs;

    &.selected {
      border: 1px solid $color-black-light;
    }

    @include not-desktop() {
      margin-bottom: 0;
      margin-right: $space-xxxs;
    }

    @include mobile() {
      width: 65px;
      height: 65px;
    }
  }

  // The overlay is sized as a percentage of this box, so any padding here would scale it off the plate face
  :deep(.content.center) {
    padding: 0;
  }
}

</style>
